/**
 * Privacy policy copy.
 *
 * Written to describe what this site actually does, not a generic template:
 * two forms that are emailed to us (server/routes/contact.ts, careers.ts), an
 * in-memory IP rate limiter, no analytics or cookies of our own, and one
 * embedded Google Map on the contact page (components/ui/LocationMap.tsx) —
 * the site's only third-party request. Booking buttons open Cal.com in the current
 * tab rather than loading it here, so they fetch nothing from this site.
 *
 * If any of that changes, this file has to change with it: a policy that
 * overstates or understates the handling is worse than none.
 *
 * `lastUpdated` is shown to visitors; bump it whenever the substance changes.
 */

export const privacy = {
  lastUpdated: '21 September 2026',

  hero: {
    eyebrow: 'Privacy',
    headline: 'How we handle your information',
    subcopy:
      'A short, plain-language account of what we collect, why we collect it, and what we do not do. No tracking, no advertising, no selling anything on to anyone.',
  },

  /** Shown as a summary strip under the hero. */
  summary: [
    {
      k: 'Only what you send',
      v: 'We collect the details and resume you submit through our forms — nothing more',
    },
    {
      k: 'No tracking',
      v: 'No analytics and no advertising pixels; one Google Map on the contact page',
    },
    {
      k: 'Never sold',
      v: 'Your details are never sold, rented or shared for marketing',
    },
  ],

  sections: [
    {
      id: 'who-we-are',
      heading: 'Who we are',
      body: [
        'Falcon Solutions Private Limited operates this website and is the controller of the personal information described below. We work across Calgary, Canada and Lahore, Pakistan.',
        'If you have a question about this policy or about information we hold, email info@falcon-sol.com and a person — not a ticketing system — will reply.',
      ],
    },
    {
      id: 'what-we-collect',
      heading: 'What we collect',
      body: [
        'We only collect information you deliberately give us. There is no account system, no login, and no profile built about you in the background.',
      ],
      list: {
        title: 'Contact form',
        items: [
          'Your name and email address',
          'Your company, if you choose to give one — the field is optional',
          'The service category you select',
          'The message you write',
        ],
      },
      list2: {
        title: 'Careers form',
        items: [
          'Your name and email address',
          'The kind of role you are looking for',
          'A portfolio, GitHub or LinkedIn link, if you choose to give one — the field is optional',
          'The note you write about yourself',
          'Your resume as a PDF, required to submit an application',
          'Your answer to the consent checkbox about keeping your details on file',
        ],
      },
      after: [
        'Both forms include a hidden field that real visitors never see. It exists to catch automated spam, and it is discarded rather than stored.',
      ],
    },
    {
      id: 'how-we-use-it',
      heading: 'How we use it',
      body: [
        'Contact enquiries are used to reply to you and to have the conversation you started — understanding your project, scoping it, and quoting for it. Careers submissions are used to consider you for current openings and, if you ticked the consent box, to get back in touch when something suitable comes up.',
        'We do not use your details for marketing. We do not add you to a mailing list. We will not send you anything you did not ask for.',
      ],
    },
    {
      id: 'legal-basis',
      heading: 'Why we are allowed to',
      body: [
        'Where the GDPR or similar law applies to you, our lawful bases are: your consent, which you give by submitting the form and, for keeping a job application on file, by ticking the consent box; and our legitimate interest in responding to enquiries about our services and in protecting the site from abuse.',
        'You can withdraw consent at any time by emailing us. Withdrawing it does not affect anything done before you asked.',
      ],
    },
    {
      id: 'where-it-goes',
      heading: 'Where it goes',
      body: [
        'Form submissions, including resume attachments, are delivered to our own inboxes by email. Uploaded resumes are not placed in a public website folder. This site keeps no database of enquiries or applications — once a message has been sent to us, the copy that matters is the one in our mail.',
        'That means your information passes through, and is stored by, our email provider and our hosting provider, both acting on our instructions. We do not share your details with anyone else: no advertisers, no data brokers, no third-party marketing tools. We would only disclose information where the law genuinely requires it.',
        'Because we operate in Canada and Pakistan, your information may be handled in either country, and by providers whose servers sit elsewhere. Where you are in a region with data transfer rules, we rely on your consent and on appropriate contractual safeguards with those providers.',
      ],
    },
    {
      id: 'cookies',
      heading: 'Cookies and tracking',
      body: [
        'This site sets no cookies of its own. There is no Google Analytics, no advertising pixel and no session tracker.',
        'There is one piece of third-party content: the contact page embeds a Google Map showing our offices. Opening that page loads the map from Google, which means Google is told you visited it and may set its own cookies inside that frame under its privacy policy rather than ours. The map is centred on our cities only, and we pass nothing about you to it. Blocking third-party frames in your browser leaves the rest of the page working normally.',
        'Our booking buttons open Cal.com in the current tab rather than loading it into this site, so nothing is requested from Cal.com while you are here. Once you are on their page you are their visitor, and anything you enter to make a booking is handled under their privacy policy.',
        'Our web server keeps short-lived technical records of requests, including IP addresses, which are used to apply a rate limit to the two forms and to keep the site available. These are not used to identify or profile visitors.',
        'Fonts are served from this site rather than a third-party font network, so loading a page does not report your visit to a font provider.',
      ],
    },
    {
      id: 'how-long',
      heading: 'How long we keep it',
      body: [
        'Enquiry emails are kept while the conversation is live, and afterwards as a business record of work we discussed or carried out. Job applications, including attached resumes, are kept for as long as we are considering you and, where you consented, for up to two years so that we can contact you about future openings.',
        'Ask us to delete something and we will, unless we are legally required to keep it.',
      ],
    },
    {
      id: 'your-rights',
      heading: 'Your rights',
      body: [
        'Depending on where you live, you may have the right to ask us for a copy of the information we hold about you, to have it corrected, to have it deleted, to restrict or object to how we use it, and to withdraw consent you previously gave.',
        'Email info@falcon-sol.com to exercise any of these. We will not charge you for it and we will respond within 30 days. If you are unhappy with our answer, you can complain to your local data protection authority — in Canada, the Office of the Privacy Commissioner.',
      ],
    },
    {
      id: 'security',
      heading: 'Security',
      body: [
        'The site is served over HTTPS, form input is validated on both the browser and the server, and the forms are rate limited against abuse. Access to the inboxes that receive your messages is limited to the people who need it.',
        'No system is perfectly secure, and we would rather say so than claim otherwise. If you have something genuinely sensitive to share, ask us first and we will arrange a better channel than a web form.',
      ],
    },
    {
      id: 'children',
      heading: "Children's information",
      body: [
        'This site is aimed at businesses and at people seeking work with us. It is not directed at children, and we do not knowingly collect information from anyone under 16. If you believe a child has sent us something, tell us and we will delete it.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to this policy',
      body: [
        'If we change how we handle information — adding analytics, for example, or storing submissions in a database — we will update this page and change the date at the top before the change takes effect.',
      ],
    },
  ],

  cta: {
    heading: 'Questions about your data?',
    body: 'Ask us directly. A real person will answer, usually within one working day.',
    button: { label: 'Get in touch', href: '/contact' },
  },
} as const;
